import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, LogOut, Users, DollarSign, Repeat, Plus, Trash2, Upload } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MediaKitTab from "../components/MediaKitTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ leads: 0, paid_transactions: 0, active_retainers: 0 });
  const [subs, setSubs] = useState([]);
  const [txns, setTxns] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(true);
  const [caseFiles, setCaseFiles] = useState([]);
  const [cfForm, setCfForm] = useState({ id: "", city: "", title: "", services: "", image_base64: "", sort_order: 100 });
  const [cfSaving, setCfSaving] = useState(false);
  const cfFileRef = useRef();
  const [site, setSite] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);
  const [liveOps, setLiveOps] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [l, s, subsRes, txnRes, cfRes, siteRes, opsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/transactions`),
        axios.get(`${API}/case-files`),
        axios.get(`${API}/site-content`),
        axios.get(`${API}/admin/live-ops`).catch(() => ({ data: null })),
      ]);
      setLeads(l.data || []);
      setStats(s.data);
      setSubs(subsRes.data.subscriptions || []);
      setTxns(txnRes.data.transactions || []);
      setCaseFiles(cfRes.data.case_files || []);
      setSite(siteRes.data);
      setLiveOps(opsRes.data);
    } finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    const t = setInterval(() => {
      axios.get(`${API}/admin/live-ops`).then(r => setLiveOps(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() { await logout(); nav("/login", { replace: true }); }

  function onCfFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      toast.error(`Photo too large (${(f.size/1024/1024).toFixed(1)} MB). Max 15 MB.`);
      return;
    }
    if (!f.type.startsWith("image/")) {
      toast.error("That's not an image file. JPG / PNG / WEBP only.");
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      setCfForm((s) => ({ ...s, image_base64: r.result }));
      toast.success(`Photo attached (${(f.size/1024).toFixed(0)} KB). Now click "Save Case File" below.`);
    };
    r.onerror = () => toast.error("Could not read the photo. Try a different file.");
    r.readAsDataURL(f);
  }

  async function saveCaseFile() {
    const missing = [];
    if (!cfForm.id)    missing.push("File ID");
    if (!cfForm.city)  missing.push("City · State");
    if (!cfForm.title) missing.push("Title");
    if (missing.length) {
      toast.error(`Missing: ${missing.join(", ")}. Fill these before saving.`);
      return;
    }
    setCfSaving(true);
    try {
      await axios.post(`${API}/admin/case-files`, {
        id: cfForm.id, city: cfForm.city, title: cfForm.title,
        services: cfForm.services.split(",").map((x) => x.trim()).filter(Boolean),
        image_base64: cfForm.image_base64, sort_order: Number(cfForm.sort_order) || 100,
      });
      toast.success(`Saved. Case file ${cfForm.id} is live on /gallery.`);
      setCfForm({ id: "", city: "", title: "", services: "", image_base64: "", sort_order: 100 });
      if (cfFileRef.current) cfFileRef.current.value = "";
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(`Save failed: ${msg}`);
    } finally { setCfSaving(false); }
  }

  async function deleteCaseFile(id) {
    if (!window.confirm(`Delete case file ${id}?`)) return;
    try { await axios.delete(`${API}/admin/case-files/${id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  }

  return (
    <div data-testid="admin-page" style={{ background: "var(--ivory)" }}>
      <section className="pt-12 pb-6">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>Operations · {user?.email}</div>
            <h1 className="font-display text-4xl md:text-6xl mt-2" style={{ letterSpacing: "-0.03em" }}>Lead <span className="font-display-italic" style={{ color: "var(--brass)" }}>console.</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="btn-ghost"><RefreshCw size={14} /> Refresh</button>
            <button onClick={handleLogout} data-testid="admin-logout" className="btn-ghost"><LogOut size={14} /> Sign Out</button>
          </div>
        </div>
      </section>

      <section className="pb-6">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total Leads" value={stats.leads} testid="stat-leads" />
          <StatCard icon={DollarSign} label="Paid Transactions" value={stats.paid_transactions} testid="stat-txns" />
          <StatCard icon={Repeat} label="Active Retainers" value={stats.active_retainers} testid="stat-subs" />
        </div>
      </section>

      <section className="pb-6" data-testid="live-ops-widget">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="thin-border p-5 sm:p-6" style={{ background: "var(--ivory-2)" }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--muted)" }}>Live Ops · Last 24 Hours</div>
                <div className="font-display text-2xl mt-1" style={{ letterSpacing: "-0.02em" }}>
                  {liveOps ? (
                    <>
                      <span style={{ color: "var(--brass)" }}>{liveOps.founding?.claimed ?? 0}</span>
                      <span className="text-base" style={{ color: "var(--muted)" }}> / 100 Founding claimed</span>
                    </>
                  ) : "Loading…"}
                </div>
              </div>
              <div className="flex items-center gap-2 font-mono text-[10px]" style={{ color: "var(--muted)" }}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: "#22c55e", boxShadow: "0 0 8px #22c55e" }}></span>
                LIVE · updates every 30s
              </div>
            </div>

            {liveOps && (
              <div className="grid md:grid-cols-3 gap-4">
                <LiveOpsPanel title="Recent Leads" items={liveOps.recent_leads} accent="var(--brass)" empty="No leads yet — share the site to get moving." render={(l) => (
                  <div>
                    <div className="font-display text-base">{l.name || "—"}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{l.email} · {l.phone}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--navy-soft)" }}>{(l.services || []).join(", ") || "—"}</div>
                    {l.source === "founding-partner-landing" && (
                      <span className="mt-1 inline-block font-mono text-[9px] uppercase tracking-[0.24em] px-1.5 py-0.5 rounded" style={{ background: "var(--brass)", color: "#fff" }}>Founding</span>
                    )}
                  </div>
                )} />
                <LiveOpsPanel title="Recent Payments" items={liveOps.recent_payments} accent="#22c55e" empty="No payments yet." render={(t) => (
                  <div>
                    <div className="font-display text-base">${((t.amount || 0) / 1).toFixed(2)} {(t.currency || "usd").toUpperCase()}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{t.customer_email || "—"}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--navy-soft)" }}>{t.metadata?.lookup_key || t.lookup_key || "—"}</div>
                    <span className="mt-1 inline-block font-mono text-[9px] uppercase tracking-[0.24em] px-1.5 py-0.5 rounded" style={{ background: t.payment_status === "paid" ? "#22c55e" : "var(--muted)", color: "#fff" }}>{t.payment_status || "pending"}</span>
                  </div>
                )} />
                <LiveOpsPanel title="Newsletter Subs" items={liveOps.recent_subs} accent="#3b82f6" empty="No subs yet." render={(s) => (
                  <div>
                    <div className="font-display text-base">{s.email}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{s.name || "—"} · {s.market || "—"}</div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--navy-soft)" }}>{(s.interests || []).join(", ") || "—"}</div>
                  </div>
                )} />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="pb-4">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12 flex items-center gap-2 border-b" style={{ borderColor: "var(--line)" }}>
          {[{ id: "leads", label: "Leads" }, { id: "subs", label: "Retainers" }, { id: "txns", label: "Transactions" }, { id: "cases", label: "Case Files" }, { id: "content", label: "Site Content" }, { id: "media", label: "Media Kit" }].map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.24em] border-b-2 -mb-px transition-colors"
              style={{ borderColor: tab === t.id ? "var(--brass)" : "transparent", color: tab === t.id ? "var(--brass)" : "var(--navy)" }}>
              {t.label}
            </button>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 lg:px-12">
          {tab === "leads" && (
            <div className="thin-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--ivory-2)" }}>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-left" style={{ color: "var(--muted)" }}>
                    <th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Services</th><th className="p-4">Property</th><th className="p-4">Date</th><th className="p-4">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>Loading…</td></tr>}
                  {!loading && leads.length === 0 && <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>No leads yet.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t" style={{ borderColor: "var(--line)" }} data-testid={`lead-row-${l.id}`}>
                      <td className="p-4"><div className="font-display text-lg">{l.name}</div>{l.company && <div className="text-xs" style={{ color: "var(--muted)" }}>{l.company}</div>}</td>
                      <td className="p-4"><div>{l.email}</div><div className="text-xs" style={{ color: "var(--muted)" }}>{l.phone}</div></td>
                      <td className="p-4 text-xs" style={{ color: "var(--navy-soft)" }}>{(l.services || []).join(", ") || "—"}</td>
                      <td className="p-4 text-xs" style={{ color: "var(--navy-soft)" }}>{l.property_type} {l.property_size && `· ${l.property_size}`}</td>
                      <td className="p-4 text-xs" style={{ color: "var(--navy-soft)" }}>{l.preferred_date || "—"}</td>
                      <td className="p-4 text-xs font-mono" style={{ color: "var(--muted)" }}>{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "subs" && (
            <div className="thin-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--ivory-2)" }}>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-left" style={{ color: "var(--muted)" }}>
                    <th className="p-4">Customer</th><th className="p-4">Package</th><th className="p-4">Monthly</th><th className="p-4">Status</th><th className="p-4">Next Bill</th><th className="p-4">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.length === 0 && <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>No retainers yet.</td></tr>}
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                      <td className="p-4"><div className="font-display text-lg">{s.customer_name || "—"}</div><div className="text-xs" style={{ color: "var(--muted)" }}>{s.customer_email}</div></td>
                      <td className="p-4">{s.package_name}</td>
                      <td className="p-4">${s.monthly_amount}</td>
                      <td className="p-4 font-mono text-xs uppercase tracking-widest" style={{ color: s.status === "active" ? "var(--brass)" : "var(--muted)" }}>{s.status}</td>
                      <td className="p-4 text-xs" style={{ color: "var(--navy-soft)" }}>{s.next_billing_date ? new Date(s.next_billing_date).toLocaleDateString() : "—"}</td>
                      <td className="p-4 text-xs font-mono" style={{ color: "var(--muted)" }}>{new Date(s.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "txns" && (
            <div className="thin-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--ivory-2)" }}>
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-left" style={{ color: "var(--muted)" }}>
                    <th className="p-4">Customer</th><th className="p-4">Package</th><th className="p-4">Amount</th><th className="p-4">Kind</th><th className="p-4">Status</th><th className="p-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.length === 0 && <tr><td colSpan="6" className="p-6 text-center" style={{ color: "var(--muted)" }}>No transactions yet.</td></tr>}
                  {txns.map((t) => (
                    <tr key={t.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                      <td className="p-4"><div className="font-display text-lg">{t.customer_name || "—"}</div><div className="text-xs" style={{ color: "var(--muted)" }}>{t.customer_email}</div></td>
                      <td className="p-4">{t.metadata?.package_name || t.package_id}</td>
                      <td className="p-4">${t.amount}</td>
                      <td className="p-4 font-mono text-xs uppercase tracking-widest" style={{ color: "var(--navy-soft)" }}>{t.package_kind || "—"}</td>
                      <td className="p-4 font-mono text-xs uppercase tracking-widest" style={{ color: t.payment_status === "paid" ? "var(--brass)" : "var(--muted)" }}>{t.payment_status}</td>
                      <td className="p-4 text-xs font-mono" style={{ color: "var(--muted)" }}>{new Date(t.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "cases" && (
            <div className="grid md:grid-cols-12 gap-6">
              {/* Form */}
              <div className="md:col-span-5 thin-border p-6" style={{ background: "var(--ivory)" }}>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--brass)" }}>Add / Update</div>
                <h3 className="font-display text-2xl mt-2" style={{ letterSpacing: "-0.02em" }}>Case File</h3>
                <div className="mt-4 space-y-3">
                  <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-2" style={{ color: "var(--terracotta)" }}>
                    * Required fields
                  </div>
                  <input data-testid="cf-id" placeholder="* File ID (e.g. C-006)" className="field-boxed" value={cfForm.id} onChange={(e) => setCfForm({ ...cfForm, id: e.target.value })} />
                  <input data-testid="cf-city" placeholder="* City · State (e.g. Charlotte · NC)" className="field-boxed" value={cfForm.city} onChange={(e) => setCfForm({ ...cfForm, city: e.target.value })} />
                  <textarea data-testid="cf-title" placeholder="* Title — use \n for line break" className="field-boxed min-h-[70px]" value={cfForm.title} onChange={(e) => setCfForm({ ...cfForm, title: e.target.value })} />
                  <input data-testid="cf-services" placeholder="Services (comma separated)" className="field-boxed" value={cfForm.services} onChange={(e) => setCfForm({ ...cfForm, services: e.target.value })} />
                  <input data-testid="cf-order" type="number" placeholder="Sort order (lower = first)" className="field-boxed" value={cfForm.sort_order} onChange={(e) => setCfForm({ ...cfForm, sort_order: e.target.value })} />
                  <label className="block cursor-pointer thin-border p-4 text-center" style={{ borderStyle: "dashed", borderColor: cfForm.image_base64 ? "var(--brass)" : "var(--line-2)", background: cfForm.image_base64 ? "rgba(232,217,197,0.15)" : "transparent" }}>
                    <input ref={cfFileRef} type="file" accept="image/*" onChange={onCfFile} className="hidden" data-testid="cf-image" />
                    {cfForm.image_base64 ? (
                      <>
                        <img src={cfForm.image_base64} alt="preview" className="w-full h-32 object-cover" />
                        <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>✓ Photo attached · click to change</div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-2"><Upload size={20} style={{ color: "var(--brass)" }} /><span className="font-mono text-[10px] uppercase tracking-[0.24em]">Attach photo (optional) · JPG/PNG · Max 15 MB</span></div>
                    )}
                  </label>
                  <button data-testid="cf-save" onClick={saveCaseFile} disabled={cfSaving} className="btn-primary w-full justify-center" style={{ boxShadow: cfForm.image_base64 ? "0 0 20px rgba(232,217,197,0.5)" : "none" }}><Plus size={14} /> {cfSaving ? "Saving…" : "Save Case File"}</button>
                </div>
              </div>

              {/* List */}
              <div className="md:col-span-7 space-y-3">
                {caseFiles.length === 0 && <div className="thin-border p-8 text-center" style={{ color: "var(--muted)" }}>No case files yet.</div>}
                {caseFiles.map((c) => (
                  <div key={c.id} className="thin-border p-3 flex items-center gap-4" style={{ background: "var(--ivory)" }} data-testid={`cf-row-${c.id}`}>
                    <div className="w-24 h-16 flex-shrink-0 overflow-hidden thin-border" style={{ background: "var(--ivory-2)" }}>
                      {c.image_url && <img src={c.image_url} alt={c.title} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[9px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>{c.id} · {c.city}</div>
                      <div className="font-display text-lg truncate" style={{ letterSpacing: "-0.02em" }}>{(c.title || "").split("\n")[0]}</div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{(c.services || []).join(" · ")}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCfForm({ id: c.id, city: c.city, title: c.title, services: (c.services || []).join(", "), image_base64: c.image_url?.startsWith("data:") ? c.image_url : "", sort_order: c.sort_order || 100 })}
                        className="font-mono text-[10px] uppercase tracking-[0.24em] px-3 py-2 border"
                        style={{ borderColor: "var(--line-2)" }}
                        data-testid={`cf-edit-${c.id}`}
                      >Edit</button>
                      <button onClick={() => deleteCaseFile(c.id)} className="h-9 w-9 flex items-center justify-center border" style={{ borderColor: "var(--terracotta)", color: "var(--terracotta)" }} data-testid={`cf-delete-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tab === "content" && site && (
            <SiteContentEditor site={site} onSaved={load} setSite={setSite} saving={siteSaving} setSaving={setSiteSaving} />
          )}
          {tab === "media" && <MediaKitTab />}
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="thin-border p-6 flex items-center gap-4" data-testid={testid} style={{ background: "var(--ivory)" }}>
      <div className="w-12 h-12 flex items-center justify-center" style={{ background: "var(--brass)", color: "var(--text)" }}>
        <Icon size={18} />
      </div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--muted)" }}>{label}</div>
        <div className="font-display text-4xl" style={{ letterSpacing: "-0.03em" }}>{value}</div>
      </div>
    </div>
  );
}

function LiveOpsPanel({ title, items, accent, empty, render }) {
  const list = items || [];
  return (
    <div className="p-4 rounded" style={{ background: "#fff", border: "1px solid var(--line)" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--muted)" }}>{title}</div>
        <div className="font-mono text-[10px] px-2 py-0.5 rounded" style={{ background: accent, color: "#fff" }}>{list.length}</div>
      </div>
      {list.length === 0 ? (
        <div className="text-xs py-6 text-center" style={{ color: "var(--muted)" }}>{empty}</div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
          {list.slice(0, 6).map((item, i) => (
            <div key={item.id || item.session_id || item.email || i} className="pb-3 border-b last:border-0 last:pb-0" style={{ borderColor: "var(--line)" }}>
              {render(item)}
              <div className="text-[10px] font-mono mt-1" style={{ color: "var(--muted)" }}>{new Date(item.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SiteContentEditor({ site, setSite, saving, setSaving, onSaved }) {
  const heroRef = useRef();
  const founderRef = useRef();
  const videoRef = useRef();

  function setField(k, v) { setSite((s) => ({ ...s, [k]: v })); }
  function setValue(i, k, v) {
    setSite((s) => {
      const values = [...(s.values || [])];
      values[i] = { ...values[i], [k]: v };
      return { ...s, values };
    });
  }
  function addValue() { setSite((s) => ({ ...s, values: [...(s.values || []), { title: "", body: "" }] })); }
  function removeValue(i) { setSite((s) => ({ ...s, values: (s.values || []).filter((_, idx) => idx !== i) })); }

  async function uploadMedia(field, file) {
    if (!file) return;
    const r = new FileReader();
    r.onload = async () => {
      try {
        await axios.post(`${API}/admin/site-content/media`, { field, data_uri: r.result });
        setField(field, r.result);
        toast.success("Media uploaded.");
      } catch { toast.error("Upload failed."); }
    };
    r.readAsDataURL(file);
  }

  async function saveAll() {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/site-content`, {
        about_headline: site.about_headline,
        about_body: site.about_body,
        founder_name: site.founder_name,
        founder_title: site.founder_title,
        founder_bio: site.founder_bio,
        mission: site.mission,
        values: site.values,
      });
      toast.success("About page saved.");
      onSaved?.();
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  }

  return (
    <div className="space-y-8">
      <div className="thin-border p-6" style={{ background: "var(--ivory)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--brass)" }}>Hero Media</div>
        <h3 className="font-display text-2xl mt-2" style={{ letterSpacing: "-0.02em" }}>Landing Hero — Image or Video</h3>
        <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Upload one image OR a short silent video (mp4/webm, ~10 MB max). Video takes priority if both are set.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="thin-border p-4 flex items-center justify-between gap-4" style={{ borderStyle: "dashed" }}>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>Hero Image</div>
              {site.hero_image_url ? <img src={site.hero_image_url} alt="hero" className="mt-2 h-24 w-full object-cover" /> : <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>No hero image set</div>}
            </div>
            <input ref={heroRef} type="file" accept="image/*" onChange={(e) => uploadMedia("hero_image_url", e.target.files?.[0])} className="hidden" data-testid="hero-image-input" />
            <button onClick={() => heroRef.current?.click()} type="button" className="btn-ghost">Upload</button>
          </div>
          <div className="thin-border p-4 flex items-center justify-between gap-4" style={{ borderStyle: "dashed" }}>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>Hero Video (loop)</div>
              {site.hero_video_url ? <video src={site.hero_video_url} muted loop playsInline className="mt-2 h-24 w-full object-cover" /> : <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>No video set</div>}
            </div>
            <input ref={videoRef} type="file" accept="video/mp4,video/webm" onChange={(e) => uploadMedia("hero_video_url", e.target.files?.[0])} className="hidden" data-testid="hero-video-input" />
            <button onClick={() => videoRef.current?.click()} type="button" className="btn-ghost">Upload</button>
          </div>
        </div>
      </div>

      <div className="thin-border p-6" style={{ background: "var(--ivory)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--brass)" }}>About Page</div>
        <h3 className="font-display text-2xl mt-2" style={{ letterSpacing: "-0.02em" }}>Headline &amp; Body</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="overline block mb-2">Headline (use line-break; last line renders italic in brass)</label>
            <textarea data-testid="about-headline" className="field-boxed min-h-[70px]" value={site.about_headline || ""} onChange={(e) => setField("about_headline", e.target.value)} />
          </div>
          <div>
            <label className="overline block mb-2">Body (double line-break to start a new paragraph)</label>
            <textarea data-testid="about-body" className="field-boxed min-h-[180px]" value={site.about_body || ""} onChange={(e) => setField("about_body", e.target.value)} />
          </div>
          <div>
            <label className="overline block mb-2">Mission (single sentence — appears as huge pull-quote)</label>
            <textarea data-testid="about-mission" className="field-boxed min-h-[80px]" value={site.mission || ""} onChange={(e) => setField("mission", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="thin-border p-6" style={{ background: "var(--ivory)" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--brass)" }}>Founder</div>
        <div className="grid md:grid-cols-12 gap-4 mt-4">
          <div className="md:col-span-4">
            <div className="thin-border p-4 block" style={{ borderStyle: "dashed" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--brass)" }}>Portrait</div>
              {site.founder_image_url ? <img src={site.founder_image_url} alt="founder" className="mt-2 aspect-[4/5] w-full object-cover" /> : <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>No portrait set</div>}
              <input ref={founderRef} type="file" accept="image/*" onChange={(e) => uploadMedia("founder_image_url", e.target.files?.[0])} className="hidden" data-testid="founder-image-input" />
              <button onClick={() => founderRef.current?.click()} type="button" className="btn-ghost w-full justify-center mt-2">Upload Portrait</button>
            </div>
          </div>
          <div className="md:col-span-8 space-y-3">
            <input data-testid="founder-name" className="field-boxed" placeholder="Founder name" value={site.founder_name || ""} onChange={(e) => setField("founder_name", e.target.value)} />
            <input data-testid="founder-title" className="field-boxed" placeholder="Title (e.g. Founder & Operator)" value={site.founder_title || ""} onChange={(e) => setField("founder_title", e.target.value)} />
            <textarea data-testid="founder-bio" className="field-boxed min-h-[100px]" placeholder="Short bio" value={site.founder_bio || ""} onChange={(e) => setField("founder_bio", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="thin-border p-6" style={{ background: "var(--ivory)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em]" style={{ color: "var(--brass)" }}>Operating Principles</div>
            <h3 className="font-display text-2xl mt-2" style={{ letterSpacing: "-0.02em" }}>Values (3 recommended)</h3>
          </div>
          <button onClick={addValue} type="button" className="btn-ghost">+ Add</button>
        </div>
        <div className="mt-4 space-y-3">
          {(site.values || []).map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-start">
              <input className="field-boxed col-span-4" placeholder={`Principle ${i + 1} title`} value={v.title} onChange={(e) => setValue(i, "title", e.target.value)} />
              <textarea className="field-boxed col-span-7" placeholder="Description" value={v.body} onChange={(e) => setValue(i, "body", e.target.value)} />
              <button onClick={() => removeValue(i)} type="button" className="col-span-1 h-11 border flex items-center justify-center" style={{ borderColor: "var(--terracotta)", color: "var(--terracotta)" }}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={saveAll} disabled={saving} className="btn-primary" data-testid="save-site-content">
        {saving ? "Saving…" : "Save About Page"}
      </button>
    </div>
  );
}
